import { PersonIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import PageLoader from '@components/PageLoader'
import { useMe, useUpdateMe } from '@hooks'
import { useAuth } from '@context/AuthContext'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import {
	ProfileContent,
	ProfileAvatar,
	ProfileInfo,
	ProfileName,
	ProfileEmail,
	ProfileActions,
	EditButton,
} from './ProfilePage.styles'
import EditProfilePictureModal from './EditProfilePictureModal'
import EditNameModal from './EditNameModal'
import { ErrorNote } from '@components/Notice'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import { TestIds } from '@/testIds'

const ProfilePage: React.FC = () => {
	const { setAuth, token, user: authUser } = useAuth()
	const { data: user, isLoading, error } = useMe()
	const updateMe = useUpdateMe()
	const [isEditPictureModalOpen, setIsEditPictureModalOpen] = useState(false)
	const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false)

	if (isLoading) {
		return (
			<PageScroll>
				<PageLoader label="Loading profile…" />
			</PageScroll>
		)
	}

	if (error || !user) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorNote>Error loading profile. Please try again.</ErrorNote>
				</PageColumn>
			</PageScroll>
		)
	}

	const handleUpdateName = (firstName: string, lastName: string) => {
		updateMe.mutate(
			{ firstName, lastName },
			{
				onSuccess: (updatedUser) => {
					toast.success('Name updated successfully!')
					setIsEditNameModalOpen(false)
					// Update auth context with new user data
					if (authUser) {
						setAuth({
							token,
							user: {
								...authUser,
								firstName: updatedUser.firstName || undefined,
								lastName: updatedUser.lastName || undefined,
							},
						})
					}
				},
				onError: () => {
					toast.error('Failed to update name. Please try again.')
				},
			}
		)
	}

	return (
		<PageScroll>
			<PageColumn>
				<PageHeader title="My Profile" />

				<ProfileContent>
					<ProfileAvatar>
						{user.profilePicture ? (
							<img src={user.profilePicture} alt={`${user.firstName || ''} ${user.lastName || ''}`} />
						) : (
							<PersonIcon sx={{ fontSize: iconSizes.art }} />
						)}
					</ProfileAvatar>

					<ProfileInfo>
						<ProfileName data-testid={TestIds.me.nameLabel}>
							{user.firstName || ''} {user.lastName || ''}
						</ProfileName>
						<ProfileEmail>{user.email}</ProfileEmail>
					</ProfileInfo>

					<ProfileActions>
						<EditButton onClick={() => setIsEditPictureModalOpen(true)}>
							Edit Profile Picture
						</EditButton>
						<EditButton onClick={() => setIsEditNameModalOpen(true)} data-testid={TestIds.me.editProfileButton}>
							Edit Name
						</EditButton>
					</ProfileActions>
				</ProfileContent>
			</PageColumn>

			<EditProfilePictureModal
				isOpen={isEditPictureModalOpen}
				onClose={() => setIsEditPictureModalOpen(false)}
			/>

			<EditNameModal
				isOpen={isEditNameModalOpen}
				onClose={() => setIsEditNameModalOpen(false)}
				onSave={handleUpdateName}
				initialFirstName={user.firstName || ''}
				initialLastName={user.lastName || ''}
				isUpdating={updateMe.isPending}
			/>
		</PageScroll>
	)
}

export default ProfilePage

